import { useState } from "react";
import { Link } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { Plus } from "lucide-react";
import type { Group } from "@/lib/issues";
import { patchIssue, type Issue, type ProjectData } from "@/lib/store";
import type { ViewSettings } from "@/lib/views";
import { errorMessage } from "@/lib/api";
import { toast } from "@/lib/toast";
import { AssigneePicker, PriorityPicker } from "./IssueProps";
import { LabelDot, StateIcon } from "./PropertyIcons";

/** Kanban: one column per group; native drag & drop changes the grouped property. */
export function IssueBoard({
  d,
  groups,
  view,
  onCreateInGroup,
}: {
  d: ProjectData;
  groups: Group[];
  view: ViewSettings;
  onCreateInGroup?: (g: Group) => void;
}) {
  const { t } = useTranslation();
  const [over, setOver] = useState<string | null>(null);

  const patchFor = (g: Group): Parameters<typeof patchIssue>[1] | null => {
    switch (view.groupBy) {
      case "state":
        return { state_id: g.key };
      case "priority":
        return { priority: Number(g.key) };
      case "assignee":
        return { assignee_id: g.key === "none" ? null : g.key };
      case "task":
        return { task_id: g.key === "none" ? null : g.key };
      default:
        return null;
    }
  };

  const drop = async (e: React.DragEvent, g: Group) => {
    e.preventDefault();
    setOver(null);
    const id = e.dataTransfer.getData("text/issue-id");
    const issue = d.issues[id];
    const patch = patchFor(g);
    if (!issue || !patch) return;
    // Append at the end of the column in manual order.
    const last = g.issues.filter((i) => i.id !== id).at(-1);
    try {
      await patchIssue(issue, { ...patch, ...(last ? { after_id: last.id } : {}) });
    } catch (err) {
      toast(errorMessage(err), { tone: "error" });
    }
  };

  return (
    <div className="flex flex-1 gap-3 overflow-x-auto p-3">
      {groups.map((g) => (
        <div
          key={g.key}
          className={`flex w-[280px] shrink-0 flex-col rounded-lg border ${over === g.key ? "border-accent bg-accent/5" : "border-border bg-sidebar/50"}`}
          onDragOver={(e) => {
            if (patchFor(g)) {
              e.preventDefault();
              setOver(g.key);
            }
          }}
          onDragLeave={() => setOver((o) => (o === g.key ? null : o))}
          onDrop={(e) => void drop(e, g)}
        >
          <header className="group/h flex h-9 items-center gap-2 px-3 text-[13px]">
            {g.state ? <StateIcon state={g.state} /> : g.color ? <span className="h-2.5 w-2.5 rounded-full" style={{ background: g.color }} /> : null}
            <span className="font-medium">{g.label}</span>
            <span className="text-muted">{g.issues.length}</span>
            {onCreateInGroup && (
              <button className="ml-auto rounded p-1 text-muted opacity-0 hover:bg-hover hover:text-fg group-hover/h:opacity-100" title={t("nav.newIssue")} onClick={() => onCreateInGroup(g)}>
                <Plus className="h-3.5 w-3.5" />
              </button>
            )}
          </header>
          <div className="flex flex-1 flex-col gap-1.5 overflow-y-auto px-2 pb-2">
            {g.issues.map((i) => (
              <Card key={i.id} d={d} issue={i} view={view} />
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

function Card({ d, issue, view }: { d: ProjectData; issue: Issue; view: ViewSettings }) {
  const labels = issue.label_ids.map((id) => d.labels[id]).filter((l): l is NonNullable<typeof l> => !!l);
  return (
    <div
      draggable
      onDragStart={(e) => {
        e.dataTransfer.setData("text/issue-id", issue.id);
        e.dataTransfer.effectAllowed = "move";
      }}
      className="cursor-grab rounded-md border border-border bg-elevated p-2.5 shadow-sm hover:border-border-strong active:cursor-grabbing"
    >
      <div className="flex items-center justify-between text-[11.5px] text-muted">
        <span className="font-mono">{d.project.key}-{issue.number}</span>
        {view.props.assignee && <AssigneePicker d={d} issue={issue} compact />}
      </div>
      <Link to="/$ws/issue/$identifier" params={{ ws: d.slug, identifier: `${d.project.key}-${issue.number}` }} className="mt-1 block text-[13px] leading-snug text-fg hover:underline">
        {issue.title}
      </Link>
      <div className="mt-2 flex flex-wrap items-center gap-1.5">
        <PriorityPicker issue={issue} compact />
        {view.groupBy !== "state" && <StateIcon state={d.states[issue.state_id]} />}
        {view.props.labels &&
          labels.map((l) => (
            <span key={l.id} className="inline-flex items-center gap-1 rounded-full border border-border px-1.5 py-px text-[11px] text-muted">
              <LabelDot color={l.color} /> {l.name}
            </span>
          ))}
      </div>
    </div>
  );
}
